import { useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { Loader2, Search, Check, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { DataTable, SectionHeading, PUBLISHING_STATES, ngn, usd, shortDate } from "./ui";
import { BunnyMigrationCard } from "./BunnyMigrationCard";
import AiSubgenresEditor from "./AiSubgenresEditor";

const STATUS_TONE = {
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  pending: "bg-gold/15 text-gold border-gold/30",
  rejected: "bg-red-500/15 text-red-400 border-red-500/30",
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const StateCell = ({ film, onSaved }) => {
  const [state, setState] = useState(film.publishing_state || "ready");
  const [date, setDate] = useState(film.release_date ? String(film.release_date).slice(0, 10) : "");
  const [publicAt, setPublicAt] = useState(toLocalInput(film.public_at));
  const [busy, setBusy] = useState(false);
  const approved = film.status === "approved";
  const dirty = state !== (film.publishing_state || "ready")
    || (state === "coming_soon" && date !== String(film.release_date || "").slice(0, 10))
    || (state === "premiering" && publicAt !== toLocalInput(film.public_at));

  const save = async () => {
    if (state === "coming_soon" && !date) return toast.error("Pick a release date for Coming Soon");
    const scheduled = state === "premiering" && publicAt ? new Date(publicAt) : null;
    if (scheduled && scheduled <= new Date()) return toast.error("Pick a Public date in the future");
    setBusy(true);
    try {
      await api.post(`/admin/films/${film.id}/publishing-state`, {
        state,
        release_date: state === "coming_soon" ? date : null,
        public_at: scheduled ? scheduled.toISOString() : null,
      });
      toast.success(`${film.title} → ${PUBLISHING_STATES.find((s) => s.value === state)?.label}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  if (!approved) return <span className="text-xs text-foreground/40">Approve first</span>;
  return (
    <div className="flex flex-wrap items-center gap-2 min-w-[400px]">
      <Select value={state} onValueChange={setState}>
        <SelectTrigger className="bg-ink border-white/10 h-8 w-44 text-xs" data-testid={`fm-state-select-${film.id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PUBLISHING_STATES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {state === "coming_soon" && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 w-36 justify-start bg-ink border-white/10 text-xs font-normal text-foreground hover:bg-white/5 hover:text-foreground data-[state=open]:bg-white/5" data-testid={`fm-release-date-${film.id}`}>
              <CalendarDays className="w-3.5 h-3.5 text-gold" />
              {date ? format(parseISO(date), "d MMM yyyy") : <span className="text-foreground/45">Release date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-surface border-white/10" align="start">
            <Calendar
              mode="single"
              selected={date ? parseISO(date) : undefined}
              onSelect={(d) => d && setDate(format(d, "yyyy-MM-dd"))}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      )}
      {state === "premiering" && (
        <label className="flex items-center gap-1.5 text-[11px] text-foreground/60">
          Goes Public ({Intl.DateTimeFormat().resolvedOptions().timeZone})
          <input
            type="datetime-local"
            value={publicAt}
            onChange={(e) => setPublicAt(e.target.value)}
            className="h-8 rounded-md bg-ink border border-white/10 px-2 text-xs text-foreground [color-scheme:dark]"
            data-testid={`fm-public-at-${film.id}`}
          />
          {publicAt && (
            <button type="button" onClick={() => setPublicAt("")} className="text-foreground/50 hover:text-foreground underline" data-testid={`fm-public-at-clear-${film.id}`}>clear</button>
          )}
        </label>
      )}
      {dirty && (
        <Button size="sm" onClick={save} disabled={busy} className="h-8 px-3 bg-terracotta hover:bg-ember text-white" data-testid={`fm-state-save-${film.id}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
        </Button>
      )}
    </div>
  );
};

const BUNNY_TONE = { ready: "text-emerald-400", processing: "text-gold", uploading: "text-gold", failed: "text-red-400" };

const BunnyCell = ({ film, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const v = film.bunny_video_status;
  const t = film.bunny_trailer_status;
  const canMove = (film.has_legacy_video && !["ready", "processing"].includes(v)) || (film.has_legacy_trailer && !["ready", "processing"].includes(t));
  const move = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/films/${film.id}/bunny-migrate`);
      toast.success(data.message);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start the move");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="text-xs space-y-1 min-w-[150px]" data-testid={`fm-bunny-${film.id}`}>
      <p>Film: <span className={BUNNY_TONE[v] || "text-foreground/45"}>{v || (film.has_legacy_video ? "current storage" : "—")}</span></p>
      <p>Trailer: <span className={BUNNY_TONE[t] || "text-foreground/45"}>{t || (film.has_legacy_trailer ? "current storage" : "—")}</span></p>
      {canMove && (
        <Button size="sm" variant="outline" onClick={move} disabled={busy} className="h-7 px-2 text-[11px] border-white/15" data-testid={`fm-bunny-move-${film.id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Move to Bunny
        </Button>
      )}
    </div>
  );
};

export default function FilmManagement({ films = [], onChanged }) {
  const [refreshing, setRefreshing] = useState(false);
  const refreshBunny = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/bunny/refresh");
      toast.success(`Checked ${data.refreshed} encoding job${data.refreshed === 1 ? "" : "s"}`);
      onChanged();
    } catch {
      toast.error("Could not refresh Bunny status");
    } finally {
      setRefreshing(false);
    }
  };
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const rows = useMemo(() => films.filter((f) => {
    const matches = !q || `${f.title} ${f.filmmaker_name}`.toLowerCase().includes(q.toLowerCase());
    const st = f.publishing_state || (f.status === "approved" ? "ready" : f.status);
    return matches && (stateFilter === "all" || st === stateFilter);
  }), [films, q, stateFilter]);

  return (
    <section data-testid="film-management">
      <SectionHeading eyebrow="Catalogue" title="Film management" description="Every film on Cinevoya. Change the publishing state or set a Coming Soon release date right from the table." />
      <BunnyMigrationCard onChanged={onChanged} />
      <AiSubgenresEditor />
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or filmmaker" className="pl-9 bg-surface border-white/10 h-10" data-testid="fm-search-input" />
        </div>
        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger className="bg-surface border-white/10 h-10 w-52" data-testid="fm-state-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All states</SelectItem>
            {PUBLISHING_STATES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            <SelectItem value="pending">Pending review</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={refreshBunny} disabled={refreshing} className="h-10 border-white/10 bg-surface" data-testid="fm-bunny-refresh-btn">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Refresh Bunny status
        </Button>
      </div>
      <DataTable
        testId="film-management-table"
        headers={["Film", "Uploaded", "Review", "Publishing state", "Pricing", "Bunny Stream"]}
        empty={rows.length === 0 ? "No films match." : null}
      >
        {rows.map((f) => (
          <tr key={f.id} className="hover:bg-white/[0.02]" data-testid={`fm-row-${f.id}`}>
            <td className="p-3 max-w-[220px]">
              <p className="font-medium truncate">{f.title}</p>
              <p className="text-xs text-foreground/50 truncate">{f.filmmaker_name || "—"}</p>
            </td>
            <td className="p-3 text-foreground/55 text-xs whitespace-nowrap">{shortDate(f.created_at)}</td>
            <td className="p-3"><Badge className={`border capitalize ${STATUS_TONE[f.status] || "bg-white/5 border-white/10"}`}>{f.status}</Badge></td>
            <td className="p-3"><StateCell key={`${f.publishing_state}-${f.release_date}-${f.public_at}`} film={f} onSaved={onChanged} /></td>
            <td className="p-3 text-xs whitespace-nowrap space-y-0.5">
              <span className="inline-block px-2 py-0.5 rounded-full bg-gold/10 text-gold border border-gold/30 mb-1">{ngn(f.price_naira).replace(".00", "")} tier</span>
              <p className="tabular-nums">Local {ngn(f.price_naira)}</p>
              <p className="tabular-nums text-foreground/70">African {usd(f.african_price_usd_cents)} · Diaspora {usd(f.diaspora_price_usd_cents)}</p>
            </td>
            <td className="p-3"><BunnyCell film={f} onChanged={onChanged} /></td>
          </tr>
        ))}
      </DataTable>
    </section>
  );
}
